/** Pure synchronous policy: .mts keeps client and Node 24 Functions on one implementation. */
export interface RouteVisit { id?: string; name: string; arrivalDate: string; departureDate: string }
export interface TravellerBranch<V extends RouteVisit = RouteVisit> {
  id: string; parentId: string | null; memberIds: string[]; splitVisitId: string;
  rejoinVisitId: string | null; rejoinDate: string; destinations: V[];
}
export const destinationKey = (v:{id?:string;name?:string;arrivalDate?:string}) => v.id || `${String(v.name??'').toLowerCase().replace(/[^\w\s]/g,'').replace(/\s+/g,'-')}@${String(v.arrivalDate??'')}`;
const visitId = destinationKey;
export interface RouteSectionVisit<V extends RouteVisit = RouteVisit> {scopeId:string|null; visit:V & RouteVisit; sourceVisit:V}

export interface TripParticipation { startVisitId: string; endVisitId: string; startDate: string; endDate: string }
/** Only explicit, valid local dates within the selected shared visits are accepted. */
export function validateParticipation(visits: RouteVisit[], input: unknown): TripParticipation | null {
  if (input === null || input === undefined) return null;
  const fail = () => { throw new Error('Choose where and when you join and leave the trip.'); };
  if (!input || typeof input !== 'object' || Array.isArray(input)) return fail();
  const value = input as TripParticipation;
  const date = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)
    && Number.isFinite(Date.parse(`${v}T12:00:00Z`)) && new Date(`${v}T12:00:00Z`).toISOString().slice(0, 10) === v;
  if (!date(value.startDate) || !date(value.endDate) || value.startDate > value.endDate) return fail();
  const sorted = [...visits].sort((a, b) => a.arrivalDate.localeCompare(b.arrivalDate));
  const start = sorted.findIndex(v => destinationKey(v) === value.startVisitId);
  const end = sorted.findIndex(v => destinationKey(v) === value.endVisitId);
  if (start < 0 || end < start || value.startDate < sorted[start].arrivalDate || value.startDate > sorted[start].departureDate
    || value.endDate < sorted[end].arrivalDate || value.endDate > sorted[end].departureDate) return fail();
  return { startVisitId: value.startVisitId, endVisitId: value.endVisitId, startDate: value.startDate, endDate: value.endDate };
}

/** Clip shared participation, not independently joined private extensions. Source rows remain writable. */
export function clipParticipation<V extends RouteVisit>(route: RouteSectionVisit<V>[], visits: V[], dates?: TripParticipation): RouteSectionVisit<V>[] {
  if (!dates) return route;
  const ordered = [...visits].sort((a, b) => a.arrivalDate.localeCompare(b.arrivalDate));
  const start = ordered.findIndex(v => destinationKey(v) === dates.startVisitId);
  const end = ordered.findIndex(v => destinationKey(v) === dates.endVisitId);
  return route.flatMap(row => {
    if (row.scopeId) return [row];
    const index = ordered.findIndex(v => destinationKey(v) === destinationKey(row.sourceVisit));
    if (index < start || index > end || start < 0 || end < 0) return [];
    const arrivalDate = row.visit.arrivalDate > dates.startDate ? row.visit.arrivalDate : dates.startDate;
    const departureDate = row.visit.departureDate < dates.endDate ? row.visit.departureDate : dates.endDate;
    if (arrivalDate > departureDate || (arrivalDate === departureDate && index !== end)) return [];
    return [{ ...row, visit: { ...row.visit, id: destinationKey(row.sourceVisit), arrivalDate, departureDate } }];
  });
}

export function visitForDate<V extends RouteVisit>(visits:V[],date:string):V|undefined {
  const sorted=[...visits].sort((a,b)=>a.arrivalDate.localeCompare(b.arrivalDate));
  const match=sorted.find(v=>date>=v.arrivalDate && date<v.departureDate);
  if(match) return match;
  const tomorrow=new Date(`${date}T00:00:00Z`);
  if(!Number.isFinite(tomorrow.getTime())) return undefined;
  tomorrow.setUTCDate(tomorrow.getUTCDate()+1);
  const leaving=sorted.find((v,i)=>v.departureDate===date && sorted[i+1]?.arrivalDate===tomorrow.toISOString().slice(0,10));
  if(leaving) return leaving;
  const last=sorted[sorted.length-1];
  return last && date>=last.arrivalDate && date<=last.departureDate ? last : undefined;
}
export function routeDayOwner<V extends RouteVisit>(route:RouteSectionVisit<V>[],date:string):RouteSectionVisit<V>|undefined {
  const visit=visitForDate(route.map(row=>row.visit),date);
  return route.find(row=>row.visit===visit);
}

/** An item's travellers: missing = everyone eligible (future joiners too);
 * a fixed list never grows and never resurrects someone who left. */
export const assignedTravellers=(assigned:string[]|undefined,eligible:string[]):string[] =>
  assigned ? eligible.filter(id=>assigned.includes(id)) : eligible;

/** Missing assignment = everyone eligible now (including future joiners).
 * Explicit [] = nobody; assignment never grants branch membership. */
export function visitParticipants(destinations:RouteVisit[],branches:TravellerBranch[],memberIds:string[],
  scopeId:string|null,sourceVisitId:string,date:string,assignedIds?:string[]):string[] {
  return memberIds.filter(uid=>{
    if(assignedIds && !assignedIds.includes(uid)) return false;
    const owner=routeDayOwner(resolveTravellerRoute(destinations,branches,uid),date);
    return owner?.scopeId===scopeId && visitId(owner.sourceVisit)===sourceVisitId;
  });
}

export function resolveTravellerRoute<V extends RouteVisit>(destinations:V[], branches:TravellerBranch<V>[], userId:string,
  parentId:string|null=null, ancestors:string[]=[]):RouteSectionVisit<V>[] {
  if (ancestors.length > 10 || (parentId && ancestors.includes(parentId))) throw new Error('Invalid branch ancestry.');
  const parent = parentId ? branches.find(b=>b.id===parentId && b.memberIds.includes(userId)) : null;
  if (parentId && !parent) return [];
  const stops=[...(parent?.destinations ?? destinations)].sort((a,b)=>a.arrivalDate.localeCompare(b.arrivalDate));
  const result:RouteSectionVisit<V>[]=[];
  let arrival:string|undefined;
  const section=(sourceVisit:V, start=sourceVisit.arrivalDate, end=sourceVisit.departureDate):RouteSectionVisit<V> => ({
    scopeId:parentId,sourceVisit,
    visit:start===sourceVisit.arrivalDate && end===sourceVisit.departureDate ? sourceVisit
      : {...sourceVisit,id:visitId(sourceVisit),arrivalDate:start,departureDate:end},
  });
  for(let i=0;i<stops.length;i++) {
    const source=stops[i], current=section(source,arrival);
    arrival=undefined;
    result.push(current);
    const branch=branches.find(b=>b.parentId===parentId && b.splitVisitId===visitId(source) && b.memberIds.includes(userId));
    if(!branch) continue;
    const own=resolveTravellerRoute(destinations,branches,userId,branch.id,[...ancestors,...(parentId?[parentId]:[])]);
    const start=own[0]?.visit.arrivalDate;
    if(start && start<=current.visit.arrivalDate) result.pop();
    else if(start && start<current.visit.departureDate) result[result.length-1]=section(source,current.visit.arrivalDate,start);
    result.push(...own);
    // A nested separate finish also ends the traveller's enclosing route.
    if(!branch.rejoinVisitId || own.some(row=>branches.some(b=>b.id===row.scopeId && !b.rejoinVisitId))) break;
    const join=stops.findIndex(s=>visitId(s)===branch.rejoinVisitId);
    if(join<i) throw new Error('Invalid branch rejoin.');
    if(join===i) {
      if(branch.rejoinDate<source.departureDate) result.push(section(source,branch.rejoinDate));
    } else {arrival=branch.rejoinDate;i=join-1;}
  }
  return result;
}

export interface ConflictParticipation { participantIds:string[]; ownerId?:string; adminIds?:string[] }
/** Callers resolve Everyone and route availability before comparing intervals. */
export function conflictingTravellers(a:ConflictParticipation,b:ConflictParticipation):string[] {
  return [...new Set(a.participantIds)].filter(id=>b.participantIds.includes(id));
}
export function conflictRecipients(a:ConflictParticipation,b:ConflictParticipation,currentMembers:string[]):string[] {
  const affected=conflictingTravellers(a,b);
  if(!affected.length) return [];
  return [...new Set([...affected,a.ownerId,b.ownerId,...(a.adminIds??[]),...(b.adminIds??[])])]
    .filter((id):id is string=>!!id && currentMembers.includes(id));
}

/** No private place/branch names in errors: root organisers may not read them. */
export function validateTravellerRoutes(destinations:RouteVisit[],branches:TravellerBranch[],memberIds:string[]):void {
  const fail=()=>{throw new Error('A traveller has conflicting destination assignments.');};
  for(const uid of memberIds) {
    const own=branches.filter(b=>b.memberIds.includes(uid));
    for(const branch of own) {
      const parent=branch.parentId?own.find(b=>b.id===branch.parentId):null;
      if(branch.parentId && !parent) fail();
      const stops=parent?.destinations ?? destinations;
      const split=stops.findIndex(v=>visitId(v)===branch.splitVisitId);
      const join=stops.findIndex(v=>visitId(v)===branch.rejoinVisitId);
      // A parent edit must not make the resolver fabricate dates outside the
      // source visit. Empty retained branches are intentionally outside `own`.
      if(split<0 || (branch.rejoinVisitId && (join<split
        || branch.rejoinDate<stops[join].arrivalDate
        || branch.rejoinDate>stops[join].departureDate))) fail();
    }
    for(let i=0;i<own.length;i++) for(const other of own.slice(i+1)) {
      const a=own[i];
      if(a.parentId!==other.parentId) continue;
      const start=a.destinations[0]?.arrivalDate, end=a.rejoinVisitId?a.rejoinDate:'9999-12-31';
      const otherStart=other.destinations[0]?.arrivalDate, otherEnd=other.rejoinVisitId?other.rejoinDate:'9999-12-31';
      if(start && otherStart && start<otherEnd && otherStart<end) fail();
    }
    const route=resolveTravellerRoute(destinations,branches,uid);
    if(route.some((row,i)=>i>0 && row.visit.arrivalDate<route[i-1].visit.departureDate)) fail();
    const visited=new Set(route.flatMap(row=>row.scopeId?[row.scopeId]:[]));
    for(const id of [...visited]) {
      let parent=branches.find(b=>b.id===id)?.parentId;
      const seen=new Set<string>();
      while(parent && !seen.has(parent)){seen.add(parent);visited.add(parent);parent=branches.find(b=>b.id===parent)?.parentId;}
    }
    if(own.some(b=>!visited.has(b.id))) fail();
  }
}
